package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initGreenIsNotCheckedPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/GreenIsNotChecked.md", FrontMatterElement.Builder { addScalar("title", "Green is not the same as checked"); addScalar("searchTitle", "S3-compatible object store on the JVM — three checks that were green and wrong"); addScalar("date", "2026-08-19"); addScalar("description", "A lock that only ever answered yes, an out-of-scope label with eight defects behind it, and a benchmark the optimiser deleted."); addScalar("library", "bochka"); }))
}

@Page("/blog/green-is-not-checked")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun GreenIsNotCheckedPage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/bochka") {
            org.jetbrains.compose.web.dom.Text("bochka")
        }
        org.jetbrains.compose.web.dom.Text(" is an S3-compatible object store on the JVM: one process, one node, one disk. It passes 426 of the 744 cases in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ceph/s3-tests")
        }
        org.jetbrains.compose.web.dom.Text(", answers ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aws-cli")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("boto3")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("mc")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("rclone")
        }
        org.jetbrains.compose.web.dom.Text(", and runs inside a test in the same process as the code under test.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Adding lifecycle rules to it took one milestone. Finding out what the existing checks had not been checking took two more, and that was the useful part. Three of them had been green the whole time.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("a-lock-that-only-ever-answered-yes") }) {
        org.jetbrains.compose.web.dom.Text("A lock that only ever answered \"yes\"")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Object lock had shipped a milestone earlier with fifteen tests. Every one of them asked whether the lock holds: put a retention on a version, try to delete it, expect a refusal. They all passed, and they all pass today.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("They could not see that the lock opened. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("PutObjectRetention")
        }
        org.jetbrains.compose.web.dom.Text(" refuses a change that ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("weakens")
        }
        org.jetbrains.compose.web.dom.Text(" the retention, and the check compared dates — later is fine, earlier is not. Changing ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("COMPLIANCE")
        }
        org.jetbrains.compose.web.dom.Text(" to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("GOVERNANCE")
        }
        org.jetbrains.compose.web.dom.Text(" keeps the date exactly as it was. Nothing was weakened by the only measure being applied, so the request went through, and a promise nobody can break became one anybody can.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The second half was worse and quieter. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CreateMultipartUpload")
        }
        org.jetbrains.compose.web.dom.Text(" takes the same ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("x-amz-object-lock-*")
        }
        org.jetbrains.compose.web.dom.Text(" headers as a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("PUT")
        }
        org.jetbrains.compose.web.dom.Text(", and the object they protect appears minutes later at the completion. The upload carried the metadata, the checksum algorithm and the checksum type across those minutes; it did not carry the lock. A multipart upload created under legal hold finished as an object anyone could delete, and the client was told the upload had succeeded — which it had.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Both are now the same shape of test, written the other way round: ask for something forbidden and require the refusal. The version is also born locked in one index record rather than locked a moment after it exists, because the gap between \"the object is there\" and \"the object is protected\" is exactly what a lock is bought to close.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("an-out-of-scope-label-is-a-defect-s-best-hiding-place") }) {
        org.jetbrains.compose.web.dom.Text("An \"out of scope\" label is a defect's best hiding place")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Most of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ceph/s3-tests")
        }
        org.jetbrains.compose.web.dom.Text(" exercises things this store does not have, so every failure carries a classified reason in a file in the repository — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("out-of-scope")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("deferred")
        }
        org.jetbrains.compose.web.dom.Text(" or ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("defect")
        }
        org.jetbrains.compose.web.dom.Text(" — and a failure matching no rule is reported by name as ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("unclassified")
        }
        org.jetbrains.compose.web.dom.Text(". That count has been zero for a long time, and the zero was doing real work: it is the number that gets watched.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The reasons underneath it were rotting. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("cors")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("tagging")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("object_lock")
        }
        org.jetbrains.compose.web.dom.Text(" all said \"out of the v1 scope\" — about features built six and ten milestones earlier. Twenty-nine cases sat behind sentences that had stopped being true, and nothing in the tooling can notice that, because a rule that matches keeps matching whatever it says.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Re-reading the reasons rather than the counts turned up ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("eight real defects")
        }
        org.jetbrains.compose.web.dom.Text(", including all three locks above and an ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("x-amz-tagging")
        }
        org.jetbrains.compose.web.dom.Text(" header that closed the connection instead of answering. None of them was found by running anything.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("An \"out of scope\" label over a defect is worse than no label at all. Unclassified is visible and asks for attention; a wrong reason looks like a closed question, and closed questions are what everyone scrolls past.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-wire-cannot-be-asked-about-nanoseconds") }) {
        org.jetbrains.compose.web.dom.Text("The wire cannot be asked about nanoseconds")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Lifecycle rules put work on the read path: every ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("GET")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("HEAD")
        }
        org.jetbrains.compose.web.dom.Text(" now looks up the bucket's rules and, if one matches, formats an ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("x-amz-expiration")
        }
        org.jetbrains.compose.web.dom.Text(" header. A milestone that touches the read path here does not close without a number, so: two machines, 1 KiB objects, presigned SigV4, seven runs of ten seconds per variant.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The result was three variants inside their own noise — and in an order that contradicts the mechanism. A bucket with no rules ought to be the cheapest; it came out between the other two. That ordering is the signature of drift between runs minutes apart, not of cost. A request that costs ~280 µs across a public link cannot be asked about a map lookup.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Measured in-process instead, on one thread: ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("8 ns")
        }
        org.jetbrains.compose.web.dom.Text(" for a bucket with no rules, 62 ns when rules exist and do not match, 555 ns when the header is built — of which the rule lookup is 62 and the rest is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("DateTimeFormatter")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The first version of that measurement said ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("2 ns")
        }
        org.jetbrains.compose.web.dom.Text(" for the no-rules case. Six cycles for two ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ConcurrentHashMap")
        }
        org.jetbrains.compose.web.dom.Text(" lookups does not happen. Every branch returned a constant zero into a sum that went nowhere, so the JIT deleted the calls, and the number was a measurement of the optimiser wearing the shape of an excellent result.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-rest-of-it") }) {
        org.jetbrains.compose.web.dom.Text("The rest of it")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("A suite case named ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("test_lifecycle_set_invalid_date")
            }
            org.jetbrains.compose.web.dom.Text(" sends ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("'20200101'")
            }
            org.jetbrains.compose.web.dom.Text(" and expects a refusal. botocore reads that as ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("epoch seconds")
            }
            org.jetbrains.compose.web.dom.Text(" and puts ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("1970-08-22T19:08:21Z")
            }
            org.jetbrains.compose.web.dom.Text(" on the wire — a perfectly well-formed date. The only thing separating it from a real one is S3's rule that the time is always midnight UTC. A test that looks like a parser check is a semantics check.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The XML reader refused self-closing elements on the grounds that accepting syntax nobody sends is surface with no purpose. The rule was right and the fact was wrong: botocore writes an empty structure and an empty string ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("only")
            }
            org.jetbrains.compose.web.dom.Text(" that way, so ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Filter {}")
            }
            org.jetbrains.compose.web.dom.Text(" arrived as a malformed document from the standard client.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("handle")
            }
            org.jetbrains.compose.web.dom.Text(" had been wrapped in try/catch since it was written. ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("screen")
            }
            org.jetbrains.compose.web.dom.Text(", which reads the request head, never was — so an exception while parsing a header left the client with a closed socket and no bytes at all. The compatibility suite reported that as ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("ConnectionClosedError")
            }
            org.jetbrains.compose.web.dom.Text(" for a whole milestone, which reads as a network fault rather than as a server that refused to answer.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("One sweep of lifecycle rules over a million versions takes ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("4.5 s")
            }
            org.jetbrains.compose.web.dom.Text(" against a period of an hour, so passes cannot overlap. Depth is cheaper than width: 4.47 µs per version across a million keys, 1.46 µs across a thousand keys a hundred versions deep.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("a-check-earns-its-green-by-being-able-to-go-red") }) {
        org.jetbrains.compose.web.dom.Text("A check earns its green by being able to go red")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Not by passing. Each of these three could pass forever without touching what it claimed to cover: a test that only asks whether a lock holds cannot see it open; a classification rule keeps matching long after its reason stops being true; a benchmark whose result the optimiser is free to delete measures the optimiser.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The practical form is a question to ask of anything green. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("What would have to break for this to go red, and is that the thing I say it protects?")
        }
        org.jetbrains.compose.web.dom.Text(" For the lock, that meant writing the tests that ask for something forbidden. For the classification, re-reading reasons instead of counts. For the benchmark, making every branch return something different and putting the sum in the output.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/bochka") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/bochka")
        }
    }
}
